import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import useScrollLock from '../../hooks/useScrollLock';
import { useLanguage } from '../../contexts/LanguageContext';
import EditMemberInfoModal from './EditMemberInfoModal';
import './Salon.css';

const ViewMembersModal = ({ isOpen, onClose, salonId, onMemberUpdated }) => {
  const { t } = useLanguage();
  const [members, setMembers] = useState([]);
  const [loading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [removingMember, setRemovingMember] = useState(null);
  const [editNameTarget, setEditNameTarget] = useState(null);

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  const fetchMembers = useCallback(async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/members`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setMembers(response.data.members || []);
    } catch (error) {
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError(t('messages.error.fetchMembers'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [salonId, t]);

  useEffect(() => {
    if (isOpen) {
      fetchMembers();
    } else {
      // Otherwise this stays set across close/reopen (the component doesn't unmount,
      // it just renders null) and the name-edit modal would reappear already open.
      setEditNameTarget(null);
    }
  }, [isOpen, fetchMembers]);

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm(t('settings.confirmRemoveMember'))) {
      return;
    }

    setRemovingMember(memberId);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/salons/${salonId}/members/${memberId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Remove from local state
      setMembers(prevMembers => 
        prevMembers.filter(member => member.id !== memberId)
      );

      if (onMemberUpdated) {
        onMemberUpdated();
      }
    } catch (error) {
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError(t('settings.failedToRemoveMember'));
      }
    } finally {
      setRemovingMember(null);
    }
  };

  const handleInfoSaved = (firstName, lastName, position, role) => {
    const memberId = editNameTarget.id;
    setMembers(prevMembers =>
      prevMembers.map(member =>
        member.id === memberId
          ? { ...member, first_name: firstName, last_name: lastName, position, role }
          : member
      )
    );
    setEditNameTarget(null);

    if (onMemberUpdated) {
      onMemberUpdated();
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'owner':
        return 'role-badge role-owner';
      case 'admin':
        return 'role-badge role-admin';
      case 'member':
        return 'role-badge role-member';
      default:
        return 'role-badge role-member';
    }
  };

  const canModifyMember = (member) => {
    // Owners can modify anyone except themselves
    // Admins can modify members but not owners or other admins
    const currentUserRole = members.find(m => m.isCurrentUser)?.role;

    if (currentUserRole === 'owner') {
      return member.role !== 'owner' || !member.isCurrentUser;
    }

    if (currentUserRole === 'admin') {
      return member.role === 'member';
    }

    return false;
  };

  // Unlike role changes/removal, editing name+position is safe to allow on your own row too -
  // it's the only way an owner (or admin) can set their own position, since that field has no
  // self-service path. Falls back to canModifyMember's existing rules for everyone else's row.
  const canEditMemberInfo = (member) => {
    if (member.isCurrentUser) {
      const currentUserRole = members.find(m => m.isCurrentUser)?.role;
      return currentUserRole === 'owner' || currentUserRole === 'admin';
    }
    return canModifyMember(member);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content view-members-modal">
        <div className="modal-header">
          <h3>{t('settings.salonMembers')}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          
          {loading ? (
            <div className="members-loading">
              <div className="spinner"></div>
              <p>{t('settings.loadingMembers')}</p>
            </div>
          ) : members.length === 0 ? (
            <div className="no-members">
              <p>{t('settings.noMembersFound')}</p>
            </div>
          ) : (
            <div className="members-list">
              {members.map((member) => (
                <div key={member.id} className="member-item">
                  <div className="member-info">
                    <div className="user-avatar">
                      {member.first_name?.[0]}{member.last_name?.[0]}
                    </div>
                    <div className="member-details">
                      <div className="member-name-row">
                        <div className="member-name">
                          {member.first_name} {member.last_name}
                        </div>
                        {canEditMemberInfo(member) && (
                          <button
                            type="button"
                            className="member-name-edit-btn"
                            onClick={() => setEditNameTarget(member)}
                            title={t('settings.editMemberName')}
                            aria-label={t('settings.editMemberName')}
                          >
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <path d="M11 2l3 3-8 8-3.5 1 1-3.5 8-8z" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        )}
                      </div>
                      <div className="member-email">{member.email}</div>
                      {member.position && <div className="member-position">{member.position}</div>}
                    </div>
                  </div>
                  
                  <div className="member-actions">
                    <span className={getRoleBadgeClass(member.role)}>
                      {member.role}
                    </span>
                    
                    {canModifyMember(member) && (
                      <div className="member-controls">
                        <button
                          className="remove-member-btn"
                          onClick={() => handleRemoveMember(member.id)}
                          disabled={removingMember === member.id}
                          title={t('settings.removeMemberFromSalon')}
                        >
                          {removingMember === member.id ? t('settings.removing') : t('settings.remove')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="modal-button secondary" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>

      <EditMemberInfoModal
        isOpen={!!editNameTarget}
        onClose={() => setEditNameTarget(null)}
        salonId={salonId}
        member={editNameTarget}
        canChangeRole={editNameTarget ? canModifyMember(editNameTarget) : false}
        onSaved={handleInfoSaved}
      />
    </div>
  );
};

export default ViewMembersModal;
